import type {BaseViewProps, BaseViewState} from "../base/ui_general/BaseView";
import {BaseView, BaseViewStateInitial} from "../base/ui_general/BaseView";
import {getQueryStringParameterAsUUID, calcHeightToBottomOffset} from "../base/utils/GeneralUtilities";
import type {FeedbackMessage, Transaction, TransactionInput, UUID} from "../base/ui_general/GeneralInterfaces";
import {InputStateControl} from "../base/utils/InputStateControl";
import {
    FeedbackMessageTypes,
    FrontendURLPaths,
    HTMLFormattedTextTypes,
    IDTypes,
    LabelTextMessageTypes,
    PaymentFrequencies,
    TransactionTypes,
} from "../base/Settings";
import {UserGuidance} from "../base/ui_components/UserGuidance";
import {MiddleColumn, VerticalSpacing} from "../base/ui_general/GeneralFunctionComponents";
import {RBSButton} from "../base/rbs_components/RBSButton";
import {TransactionDetailModal} from "../designcenter/modals/TransactionDetailModal.tsx";
import {getRequest_create_transaction, getRequest_get_all_transactions} from "../api/transaction.tsx";
import Accordion from "react-bootstrap/Accordion";
import Form from "react-bootstrap/Form";
import Fuse from "fuse.js";
import autobind from "autobind-decorator";
import React, {type ReactElement} from "react";
import type {AccordionEventKey} from "react-bootstrap/AccordionContext";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
import './LandingPageOverView.css';


/** This defines the properties of the LandingPageOverView. */
type LandingPageOverViewProps = BaseViewProps


/** This defines the state of the LandingPageOverView. */
interface LandingPageOverViewState extends BaseViewState {
    /** UUID of selected transaction. */
    transaction_id: UUID | undefined;
    showTransactionModal: boolean;
    transactions: Transaction[] | undefined;
    expandedTxId: AccordionEventKey;
    searchTerm: string;
    transaction_creation_modal_valid: boolean;
    payment_frequency: PaymentFrequencies;
    transaction_type: TransactionTypes;
}


/**
 * This defines the LandingPageOverView, which is presented to the user when not specifying a specific path in the
 * URL query string. Supposed to provide some introduction to the user and guidance to get started.
 * Inherits from [[`BaseView`]].
 */
export class LandingPageOverView extends BaseView<LandingPageOverViewProps, LandingPageOverViewState> {
    private readonly transactionCreationInputStateControl: InputStateControl;
    private accordionContainerRef = React.createRef<HTMLDivElement>();

    /**
     * Constructor. Initializing the view's state. Loads model id and rollback point id from browser's query string.
     * @param props The view's property object.
     */
    constructor(props: LandingPageOverViewProps) {
        super(props);

        this.state = {
            ...BaseViewStateInitial,
            transaction_id: getQueryStringParameterAsUUID(IDTypes.TRANSACTION),
            showTransactionModal: false,
            expandedTxId: null,
            searchTerm: "",
            transactions: undefined,
            transaction_creation_modal_valid: false,
            payment_frequency: PaymentFrequencies.QUARTERLY,
            transaction_type: TransactionTypes.CASH,
        };

        this.props.provideModelIDs(this.state.transaction_id);

        this.transactionCreationInputStateControl = new InputStateControl();
    }

    /**
     * If the component did mount, the required parameters that have been extracted from the query string in the
     * constructor are passed to the Main Frame.
     */
    componentDidMount() {
        this.props.provideModelIDs(this.state.transaction_id);
        this.transactionCreationInputStateControl.setGlobalValidityUpdateCallback(this.transactionCreationModalValidityUpdate);
        this.load_data();
    }

    componentWillUnmount() {
    }

    @autobind
    load_data() {
        this.processFetchRequest(
            getRequest_get_all_transactions(),
            (response: Transaction[]) => {
                this.setState({transactions: response});
            }
        );
    }


    /**
     * The PopUp appears for creating a new transaction.
     */
    @autobind
    requestNewTransactionCreation(transactionInput: TransactionInput) {
        this.processFetchRequest(
            getRequest_create_transaction(transactionInput),
            () => {
                const feedback_msg: FeedbackMessage = {
                    message_type: FeedbackMessageTypes.SUCCESS,
                    message_text: this.getLabelTextMessage(LabelTextMessageTypes.SUCCESSFUL_CREATION)
                }
                this.props.displayMessage(feedback_msg)
                this.load_data();
            }
        );
    }


    /**
     * Callback for receiving validity state of the transaction creation modal.
     * @param valid If true, the modal contains valid data to trigger a transaction creation request.
     */
    @autobind
    transactionCreationModalValidityUpdate(valid: boolean) {
        this.setState({transaction_creation_modal_valid: valid});
    }


    /**
     * Filtering of Transactions based on SearchTerm
     */
    getFilteredTransactions(): Transaction[] {
        if (!this.state.transactions) return [];
        if (!this.state.searchTerm) return this.state.transactions;

        const fuse = new Fuse(this.state.transactions, {
            keys: ["name", "description", "transaction_type"],
            threshold: 0.3,
            ignoreLocation: true,
        });

        return fuse.search(this.state.searchTerm).map((r) => r.item);
    }


    renderTransactionCreationModal(): ReactElement {
        return (
            <TransactionDetailModal
                show={this.state.showTransactionModal}
                onClose={() => this.setState({showTransactionModal: false})}
                onSubmit={(transactionInput: TransactionInput) => {
                    this.setState({showTransactionModal: false}, () => {
                        this.requestNewTransactionCreation(transactionInput);
                    });
                }}
                isValid={this.state.transaction_creation_modal_valid}
                inputStateControl={this.transactionCreationInputStateControl}
                user_language={this.props.user_language}
            />
        );
    }


    render() {
        const filteredTransactions = this.getFilteredTransactions();
        const transactionCreationModal = this.renderTransactionCreationModal();
        const accordionHeight = calcHeightToBottomOffset(
            this.props.windowInnerHeight,
            50,
            this.accordionContainerRef
        );
        return (
            <div>
                {/* Transaction Creation Modal */}
                {transactionCreationModal}

                {/* User Guidance */}
                <UserGuidance
                    caption={this.getLabelTextMessage(LabelTextMessageTypes.TRANSACTION_CREATE_OR_LOAD_CAPTION)}
                    explanation={this.getLabelTextMessage(LabelTextMessageTypes.LP_CREATE_OR_LOAD_EXPLANATION)}
                    details_html={this.getHTMLFormattedText(HTMLFormattedTextTypes.LP_CREATE_OR_LOAD_DETAILS)}
                    user_language={this.props.user_language}
                />

                <VerticalSpacing/>

                {/* Flex wrapper for MiddleColumn and scrollable Accordion */}
                <MiddleColumn cols={6}>
                    <RBSButton
                        extraStyle={{width: "100%"}}
                        label={this.getLabelTextMessage(LabelTextMessageTypes.CREATE)}
                        onClick={() => this.setState({showTransactionModal: true})}
                    />

                    <VerticalSpacing/>

                    <div style={{fontWeight: "bold"}}>
                        {this.getLabelTextMessage(LabelTextMessageTypes.OR)}
                    </div>

                    <VerticalSpacing/>

                    <RBSButton
                        extraStyle={{width: "100%"}}
                        label={this.getLabelTextMessage(LabelTextMessageTypes.LOAD)}
                        onClick={() => {
                            if (this.state.expandedTxId) {
                                this.navigate(
                                    FrontendURLPaths.DESIGN_CENTER,
                                    IDTypes.TRANSACTION,
                                    this.state.expandedTxId as UUID
                                );
                            }
                        }}
                        disabled={!this.state.expandedTxId}
                    />

                    <VerticalSpacing/>

                    {/* Searchbar */}
                    <Form.Control
                        type="text"
                        placeholder={this.getLabelTextMessage(LabelTextMessageTypes.SEARCHBAR)}
                        value={this.state.searchTerm}
                        onChange={(e) => this.setState({searchTerm: e.target.value})}
                        className="mb-2"
                    />

                    {/* Container for Accordion List, which is scrollable and fills remaining space */}
                    <div
                        ref={this.accordionContainerRef}
                        style={{
                            overflowY: 'auto',
                            height: accordionHeight !== undefined ? accordionHeight : 'auto',
                            border: '3px solid #ccc',
                            borderRadius: '8px',
                        }}
                    >
                        <Accordion
                            activeKey={this.state.expandedTxId}
                            onSelect={(eventKey) => {
                                this.setState({
                                    expandedTxId: eventKey
                                });
                            }}
                        >
                            {filteredTransactions.map((tx) => (
                                <Accordion.Item key={tx.id} eventKey={tx.id}>
                                    <Accordion.Header>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            width: '100%'
                                        }}>
                                            <div>
                                                {tx.name + ': '}
                                                <span style={{color: '#888', paddingLeft: 5}}> {tx.description}</span>
                                            </div>
                                        </div>
                                    </Accordion.Header>
                                    <Accordion.Body style={{position: 'relative', minHeight: '56px'}}>
                                        {this.state.expandedTxId === tx.id && (
                                            <div style={{position: 'absolute', bottom: 10, right: 10}}>
                                                <RBSButton
                                                    label={this.getLabelTextMessage(LabelTextMessageTypes.LOAD)}
                                                    extraStyle={{marginRight: '0px', minWidth: '120px'}}
                                                    onClick={() => this.navigate(
                                                        FrontendURLPaths.DESIGN_CENTER,
                                                        IDTypes.TRANSACTION,
                                                        tx.id
                                                    )}
                                                />
                                            </div>
                                        )}
                                        <Row>
                                            <Col style={{textAlign: 'left', paddingLeft: '12px'}}><p>
                                                <b>Name:</b> {tx.name}</p></Col>
                                            <Col style={{textAlign: 'left', paddingLeft: '12px'}}><p>
                                                <b>Type:</b> {tx.transaction_type}</p></Col>
                                            <Col style={{textAlign: 'left', paddingLeft: '12px'}}><p>
                                                <b>Frequency:</b> {tx.payment_frequency}</p></Col>
                                        </Row>
                                        <Row>
                                            <Col style={{textAlign: 'left', paddingLeft: '12px'}}><p><b>Effective
                                                Date:</b> {tx.effective_date}</p></Col>
                                            <Col style={{textAlign: 'left', paddingLeft: '12px'}}><p><b>First
                                                Payment:</b> {tx.first_payment_date}</p></Col>
                                            <Col style={{textAlign: 'left', paddingLeft: '12px'}}><p><b>Maturity
                                                Date:</b> {tx.maturity_date}</p></Col>
                                        </Row>
                                        <Row>
                                            <Col xs={12} style={{textAlign: 'left', paddingLeft: '12px'}}><p>
                                                <b>Description:</b> {tx.description}</p></Col>
                                        </Row>
                                    </Accordion.Body>
                                </Accordion.Item>
                            ))}
                        </Accordion>
                    </div>

                </MiddleColumn>
            </div>
        );
    }
}